import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, LoaderCircle, Search, UploadCloud } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api, downloadFile } from "../api";
import { MetricStrip, PageHeader, StatusBadge } from "../components";
import type { ImportJob, ListingValidationResult, MeliCategoryRecommendation, MeliPublishSchema } from "../types";

const sites = [
  ["CBT", "跨境站"], ["MLM", "墨西哥"], ["MLB", "巴西"], ["MCO", "哥伦比亚"],
  ["MLC", "智利"], ["MLA", "阿根廷"], ["MLU", "乌拉圭"],
] as const;

export function ListingUpload() {
  const [siteCode, setSiteCode] = useState("CBT");
  const [query, setQuery] = useState("");
  const [recommendations, setRecommendations] = useState<MeliCategoryRecommendation[]>([]);
  const [selected, setSelected] = useState<MeliCategoryRecommendation | null>(null);
  const [schema, setSchema] = useState<MeliPublishSchema | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ListingValidationResult | null>(null);
  const [importJob, setImportJob] = useState<ImportJob<ListingValidationResult> | null>(null);
  const [importJobId, setImportJobId] = useState(() => window.localStorage.getItem("meliforge-listing-import"));
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!importJobId) return;
    let stopped = false;
    async function poll() {
      try {
        const job = await api.importJob<ListingValidationResult>(importJobId!);
        if (stopped) return;
        setImportJob(job);
        if (job.status === "COMPLETED" && job.result) {
          setResult(job.result);
          window.localStorage.removeItem("meliforge-listing-import");
          setImportJobId(null);
        } else if (job.status === "FAILED") {
          setError(job.errorMessage || "后台校验失败。");
          window.localStorage.removeItem("meliforge-listing-import");
          setImportJobId(null);
        }
      } catch (cause) {
        if (!stopped) setError(cause instanceof Error ? cause.message : "校验进度读取失败。");
      }
    }
    void poll();
    const timer = window.setInterval(() => void poll(), 900);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [importJobId]);

  async function searchCategories() {
    if (query.trim().length < 2) return setError("请输入至少 2 个字符的商品名称或类目 ID。");
    setBusy("search"); setError(""); setSelected(null); setSchema(null); setResult(null);
    try {
      const response = await api.recommendListingCategories(siteCode, query.trim());
      setRecommendations(response.items);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "类目推荐失败。");
    } finally { setBusy(""); }
  }

  async function selectCategory(category: MeliCategoryRecommendation) {
    setSelected(category); setFile(null); setResult(null); setBusy("schema"); setError("");
    try { setSchema(await api.categoryPublishSchema(category.siteCode, category.categoryId)); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "发布规则读取失败。"); }
    finally { setBusy(""); }
  }

  async function validateFile() {
    if (!selected || !file) return;
    setBusy("validate"); setError(""); setResult(null);
    try {
      const job = await api.validateListingFile(selected.siteCode, selected.categoryId, file);
      setImportJob(job);
      setImportJobId(job.id);
      window.localStorage.setItem("meliforge-listing-import", job.id);
    }
    catch (cause) { setError(cause instanceof Error ? cause.message : "文件校验失败。"); }
    finally { setBusy(""); }
  }

  function resetSite(value: string) {
    setSiteCode(value); setRecommendations([]); setSelected(null); setSchema(null); setFile(null); setResult(null); setError("");
  }

  return <>
    <PageHeader eyebrow="LISTING PREPARATION" title="商品上架准备" />
    <div className="step-strip" aria-label="商品上架准备步骤">
      {["匹配类目", "下载模板", "填写商品", "上传校验"].map((label, index) => <div className={index <= (result ? 3 : file ? 2 : selected ? 1 : 0) ? "active" : ""} key={label}><span>{index + 1}</span>{label}</div>)}
    </div>

    <section className="workspace-band listing-category-panel">
      <div className="listing-search-grid">
        <div className="field-group"><label htmlFor="listing-site">目标站点</label><select id="listing-site" value={siteCode} onChange={(event) => resetSite(event.target.value)}>{sites.map(([code, label]) => <option key={code} value={code}>{label} · {code}</option>)}</select></div>
        <div className="field-group"><label htmlFor="listing-query">商品名称、关键词或类目 ID</label><div className="listing-search-input"><input id="listing-query" value={query} maxLength={160} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void searchCategories(); }} placeholder="例如：Wireless headphones" /><button className="button primary" disabled={busy === "search"} onClick={() => void searchCategories()}>{busy === "search" ? <LoaderCircle className="spin" size={17} /> : <Search size={17} />}匹配类目</button></div></div>
      </div>
      {error ? <div className="form-error" role="alert"><AlertTriangle size={17} />{error}</div> : null}
      {recommendations.length ? <div className="listing-recommendations"><div className="section-heading"><div><h2>推荐类目</h2><span>{recommendations.length} 个可发布末级类目</span></div></div><div className="listing-category-list">{recommendations.map((category) => <button key={category.categoryId} className={selected?.categoryId === category.categoryId ? "selected" : ""} onClick={() => void selectCategory(category)}><span><strong>{category.name}</strong><small>{category.pathText}</small></span><code>{category.categoryId}</code></button>)}</div></div> : null}
    </section>

    {selected ? <section className="workspace-band listing-template-panel">
      <div className="section-heading"><div><h2>{selected.name}</h2><span>{selected.pathText}</span></div><code>{selected.categoryId}</code></div>
      {busy === "schema" ? <div className="listing-loading"><LoaderCircle className="spin" size={18} />正在读取发布规则</div> : schema ? <>
        <MetricStrip items={[{ label: "全部字段", value: schema.summary.totalFields }, { label: "通用字段", value: schema.summary.commonFields }, { label: "类目属性", value: schema.summary.categoryAttributes }, { label: "必填", value: schema.summary.requiredFields }, { label: "条件必填", value: schema.summary.conditionalFields }]} />
        <div className="listing-template-actions"><button className="button secondary" disabled={!schema.ready} onClick={() => void downloadFile(`/api/listing/${siteCode}/${selected.categoryId}/template`, `meliforge-${siteCode}-${selected.categoryId}-listing-template.xlsx`)}><Download size={17} />下载完整发布模板</button></div>
        <div className={`drop-zone ${file ? "has-file" : ""}`} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); setFile(event.dataTransfer.files[0] ?? null); setResult(null); }}>
          <input ref={fileRef} type="file" tabIndex={-1} aria-hidden="true" accept=".xlsx" onChange={(event) => { setFile(event.target.files?.[0] ?? null); setResult(null); }} />
          {file ? <><FileSpreadsheet size={26} /><div><strong>{file.name}</strong><small>{(file.size / 1024).toFixed(1)} KB</small></div><button className="text-button" onClick={() => fileRef.current?.click()}>更换文件</button></> : <><UploadCloud size={28} /><div><strong>上传填写后的发布模板</strong><small>XLSX · 最多 10,000 行</small></div><button className="button secondary" onClick={() => fileRef.current?.click()}>选择文件</button></>}
        </div>
        <div className="task-form-actions"><button className="button primary" disabled={!file || busy === "validate" || Boolean(importJobId)} onClick={() => void validateFile()}>{busy === "validate" || importJobId ? <LoaderCircle className="spin" size={17} /> : <CheckCircle2 size={17} />}{importJobId ? `后台校验 ${importJob?.progress ?? 0}%` : "校验商品数据"}</button></div>
      </> : null}
    </section> : null}

    {result ? <section className="workspace-band listing-validation-results">
      <div className="section-heading"><div><h2>校验结果</h2><span>{result.siteCode} · {result.categoryId}</span></div><StatusBadge status={result.summary.blocked ? "BLOCKED" : "READY"} /></div>
      <MetricStrip items={[{ label: "总行数", value: result.summary.total }, { label: "可发布", value: result.summary.ready, tone: "positive" }, { label: "需确认", value: result.summary.warning }, { label: "已拦截", value: result.summary.blocked, tone: result.summary.blocked ? "negative" : "" }]} />
      <div className="table-scroll"><table className="data-table listing-result-table"><thead><tr><th>行</th><th>标题</th><th>状态</th><th>缺失或错误字段</th><th>官方值提示</th></tr></thead><tbody>{result.rows.map((row) => <tr key={row.rowNo}><td className="mono">{row.rowNo}</td><td>{row.title || "--"}</td><td><StatusBadge status={row.status} /></td><td>{row.issues.length ? row.issues.map((issue) => <span className={issue.level === "BLOCK" ? "listing-issue block" : "listing-issue"} key={`${issue.code}:${issue.field}`}>{issue.message}</span>) : "--"}</td><td>{row.issues.flatMap((issue) => issue.allowedValues ?? []).slice(0, 8).map((value) => value.name || value.id).join("、") || "--"}</td></tr>)}</tbody></table></div>
    </section> : null}
  </>;
}
